import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function TermsOfServicePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <div className="sticky top-0 z-50 bg-white border-b border-background-200">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-10">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2 shrink-0">
              <img
                src="https://static.readdy.ai/image/ff63a95a3d068db59710e28833339873/473109dd943942010dc73e384d113dfa.png"
                alt="AK Energy"
                className="h-7 w-auto"
              />
            </Link>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-800 transition-colors"
            >
              <i className="ri-arrow-left-line" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-4 md:px-6 lg:px-10 py-10 md:py-14">
        <p className="text-xs text-foreground-400 uppercase tracking-wider mb-2">Last updated: July 2026</p>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8">Terms of Service</h1>

        <div className="prose prose-sm max-w-none text-foreground-700 space-y-6">
          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">1. Acceptance of Terms</h2>
            <p>
              By accessing or using the AK Energy website and marketplace platform (&ldquo;the Platform&rdquo;), you agree to be bound by these Terms of Service (&ldquo;Terms&rdquo;). If you do not agree to these Terms, you must not access or use the Platform.
            </p>
            <p>
              AK Energy (&ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) reserves the right to modify these Terms at any time. Changes will be effective immediately upon posting. Your continued use of the Platform after any changes constitutes acceptance of the revised Terms.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">2. About the Platform</h2>
            <p>
              AK Energy operates a renewable energy marketplace that connects households, businesses, installers, manufacturers, finance providers, and energy retailers in Australia. We facilitate introductions and provide information to help users make informed decisions about renewable energy solutions.
            </p>
            <p>
              <strong>Important:</strong> AK Energy is a marketplace facilitator, not an energy provider, installer, manufacturer, or financial advisor. We do not provide, install, or warrant any products or services offered by third-party participants on our Platform. All transactions, installations, and service agreements are between you and the relevant third-party provider.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">3. Eligibility</h2>
            <p>To use the Platform, you must:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Be at least 18 years of age and capable of entering into a legally binding agreement.</li>
              <li>Provide accurate, current, and complete information when submitting enquiries or registering an account.</li>
              <li>Be located in Australia or seeking renewable energy solutions for an Australian property.</li>
              <li>Not be prohibited from using the Platform under applicable Australian laws.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">4. User Accounts</h2>
            <p>
              When you create an account on the Platform (as a participant — installer, manufacturer, finance provider, or energy retailer), you are responsible for maintaining the confidentiality of your login credentials and for all activities that occur under your account. You agree to:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Notify us immediately of any unauthorised use of your account.</li>
              <li>Ensure that all information in your account profile and listings is accurate and kept up to date.</li>
              <li>Not impersonate any person or entity or misrepresent your affiliation with any person or entity.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">5. Marketplace Listings and Enquiries</h2>
            <p>
              <strong>For Consumers (Homeowners and Businesses):</strong> By submitting an enquiry through the Platform, you consent to AK Energy sharing your enquiry details with relevant marketplace participants who may contact you with quotes and information. You are under no obligation to accept any quote or enter into any agreement.
            </p>
            <p>
              <strong>For Marketplace Participants:</strong> You agree to provide accurate information about your products, services, pricing, and credentials. You must hold all required licences, accreditations, and insurance to operate in Australia. AK Energy reserves the right to remove or suspend any listing that does not meet our quality standards.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">6. Intellectual Property</h2>
            <p>
              All content on the Platform, including text, graphics, logos, images, software, and the overall design and layout, is the property of AK Energy or its licensors and is protected by Australian and international intellectual property laws.
            </p>
            <p>You may not:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Reproduce, distribute, modify, or create derivative works from any Platform content without our prior written consent.</li>
              <li>Use any data mining, robots, or similar data gathering or extraction methods on the Platform.</li>
              <li>Use our trademarks, logos, or branding without our express permission.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">7. User Conduct</h2>
            <p>When using the Platform, you agree not to:</p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Violate any applicable Australian federal, state, or local laws and regulations.</li>
              <li>Submit false, misleading, or fraudulent information.</li>
              <li>Interfere with or disrupt the Platform, its servers, or networks.</li>
              <li>Upload or transmit viruses, malware, or any malicious code.</li>
              <li>Harass, abuse, or harm other users or marketplace participants.</li>
              <li>Use the Platform for any illegal or unauthorised purpose.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">8. Disclaimer of Warranties</h2>
            <p>
              The Platform is provided on an &ldquo;as is&rdquo; and &ldquo;as available&rdquo; basis. To the fullest extent permitted by law, AK Energy disclaims all warranties, express or implied, including but not limited to:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>The accuracy, completeness, or reliability of any information on the Platform.</li>
              <li>The quality, safety, or suitability of any products or services offered by marketplace participants.</li>
              <li>The uninterrupted or error-free operation of the Platform.</li>
              <li>Any outcome or result from using the Platform, including energy savings, financial returns, or environmental benefits.</li>
            </ul>
            <p className="mt-2">
              You acknowledge that renewable energy outcomes depend on numerous factors including location, property characteristics, installation quality, equipment specifications, weather conditions, and energy consumption patterns. AK Energy does not guarantee any specific results.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">9. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by Australian law (including the Australian Consumer Law), AK Energy shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, use, or goodwill, arising from:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Your use of or inability to use the Platform.</li>
              <li>Any products or services obtained through marketplace participants introduced via the Platform.</li>
              <li>Any conduct or content of any third party on the Platform.</li>
              <li>Unauthorised access, use, or alteration of your transmissions or content.</li>
            </ul>
            <p className="mt-2">
              Where our liability cannot be excluded, our total aggregate liability is limited to the amount you paid us (if any) for use of the Platform in the 12 months preceding the claim, or AUD $100, whichever is greater.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">10. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless AK Energy, its directors, officers, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of or in connection with:
            </p>
            <ul className="list-disc pl-5 space-y-2 mt-2">
              <li>Your use of the Platform.</li>
              <li>Your violation of these Terms.</li>
              <li>Your violation of any third-party rights, including intellectual property rights.</li>
              <li>Any dispute between you and a marketplace participant.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">11. Third-Party Links and Services</h2>
            <p>
              The Platform may contain links to third-party websites and services not owned or controlled by AK Energy. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites. You access third-party links at your own risk.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">12. Termination</h2>
            <p>
              We may terminate or suspend your access to the Platform immediately, without prior notice, for any reason including breach of these Terms. Upon termination, your right to use the Platform will cease immediately. Provisions that by their nature should survive termination (including intellectual property, disclaimer of warranties, limitation of liability, and indemnification) shall survive.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">13. Governing Law and Jurisdiction</h2>
            <p>
              These Terms are governed by and construed in accordance with the laws of New South Wales, Australia. You agree to submit to the exclusive jurisdiction of the courts of New South Wales for the resolution of any disputes arising under these Terms.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">14. Contact Information</h2>
            <p>If you have any questions about these Terms, please contact us:</p>
            <div className="mt-3 space-y-1 text-sm">
              <p><strong>Email:</strong> akenergyau@gmail.com</p>
              <p><strong>Phone:</strong> 1800 253 637</p>
              <p><strong>Post:</strong> AK Energy, GPO Box 1234, Sydney NSW 2001, Australia</p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}